import { useState, useCallback } from 'react';
import supabase from '@/supabase';
import type { SubscriptionPlan } from '@/types/entitlements';
import type { CreditPackSku } from '@/types/credit-packs';

interface CheckoutResult {
  checkoutUrl: string;
  orderId: string;
}

interface CheckoutError {
  error: string;
  currentPlan?: SubscriptionPlan;
}

interface UseCheckoutReturn {
  initiateSubscriptionCheckout: (plan: SubscriptionPlan) => Promise<void>;
  initiateCreditCheckout: (sku: CreditPackSku) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export function useCheckout(): UseCheckoutReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initiateSubscriptionCheckout = useCallback(async (plan: SubscriptionPlan) => {
    if (plan === 'free') {
      setError('Cannot checkout for free plan');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('You must be logged in to subscribe');
      }

      const response = await supabase.functions.invoke<CheckoutResult | CheckoutError>(
        'ccbill-checkout',
        {
          body: {
            type: 'subscription',
            plan,
          },
        }
      );

      if (response.error) {
        throw new Error(response.error.message || 'Failed to initiate checkout');
      }

      const data = response.data;

      if (!data) {
        throw new Error('No response from checkout service');
      }

      // Check if it's an error response
      if ('error' in data) {
        const errorData = data as CheckoutError;
        if (errorData.currentPlan) {
          throw new Error(`You already have the ${errorData.currentPlan} plan`);
        }
        throw new Error(errorData.error);
      }

      // Success - redirect to CCBill checkout
      const successData = data as CheckoutResult;
      window.location.href = successData.checkoutUrl;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      console.error('Checkout error:', message);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const initiateCreditCheckout = useCallback(async (sku: CreditPackSku) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('You must be logged in to purchase credits');
      }

      const response = await supabase.functions.invoke<CheckoutResult | CheckoutError>(
        'ccbill-checkout',
        {
          body: {
            type: 'credits',
            creditPackSku: sku,
          },
        }
      );

      if (response.error) {
        throw new Error(response.error.message || 'Failed to initiate checkout');
      }

      const data = response.data;

      if (!data) {
        throw new Error('No response from checkout service');
      }

      // Check if it's an error response
      if ('error' in data) {
        const errorData = data as CheckoutError;
        throw new Error(errorData.error);
      }

      // Success - redirect to CCBill checkout
      const successData = data as CheckoutResult;
      window.location.href = successData.checkoutUrl;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      console.error('Credit checkout error:', message);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    initiateSubscriptionCheckout,
    initiateCreditCheckout,
    isLoading,
    error,
  };
}
